import classNames from 'classnames';
import React, { TextareaHTMLAttributes, useEffect, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { IGeneral } from '@/shared/types';

type TextAreaProps = IGeneral.Modify<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  {
    id: string;
  }
>;

interface Props extends TextAreaProps {
  padding?: string;
  bordered?: boolean;
}

const DynamicTextArea: React.FC<Props> = ({
  id,
  padding,
  className,
  bordered = true,
  ...props
}) => {
  const { control, watch } = useFormContext();

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const value = watch(id);

  // effect ensures height adjusts after clearing on submission, not just on change
  useEffect(() => {
    if (textareaRef.current) {
      // reset height to get scrollHeight
      textareaRef.current.style.height = 'auto';

      const scrollHeight =
        textareaRef.current.scrollHeight + (bordered ? 2 : 0);

      // set height dynamically to fit content, but stop at 200px and show scrollbar
      const maxHeight = 150;
      textareaRef.current.style.height = `${Math.min(
        scrollHeight,
        maxHeight
      )}px`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <Controller
      name={id}
      control={control}
      render={({ field: { onChange, value } }) => (
        <textarea
          {...props}
          value={value ?? ''}
          id={`${id}-textarea-input`}
          ref={textareaRef}
          onChange={onChange}
          rows={1}
          className={classNames(
            className,
            padding ?? 'p-16',
            bordered && 'border-xs border-outline-default',
            ' resize-none rounded-xl leading-6 outline-0'
          )}
        />
      )}
    />
  );
};

export default DynamicTextArea;
